// Also needs you, on the company record (pipeline RC4; brief of 7 Oct section 4 item 6, drawing a6-account-file.html
// "Also needs you"): the reader's own triage items about this company or a brand under it, in triage's order, each
// drawn as triage's own card, so Park until, Dismiss and Why is this here are the card's own and a press here is the
// press triage makes. The list is triage's read (GET /api/triage), filtered by the company each item is about
// (PassItem.organisation, pipeline F6); nothing is ranked or judged on this page. A decision posts at once, as on every
// page but triage, and the card leaves; an option that drafts opens the item's own page, where the draft panel is.
import { useEffect, useState } from 'react';
import { triage, type PassItem, type Store, type TeamMember } from '../api';
import { ItemCard } from '../ItemCard';

/** The items a record lists: those about the company or one of its brands, in the order triage gave them. */
export function itemsAbout(items: readonly PassItem[], companyIds: ReadonlySet<string>): PassItem[] {
  return items.filter((i) => (i.organisation ? companyIds.has(i.organisation.id) : false));
}

type Load = { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; items: PassItem[]; team: TeamMember[] };

export function useAlsoNeedsYou(store: Store, companyIds: ReadonlySet<string>): [Load, (id: string) => void, (item: PassItem) => void] {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const key = [...companyIds].sort().join(',');
  useEffect(() => {
    let live = true;
    triage.list(store.tenantId)
      .then((v) => { if (live) setLoad({ kind: 'ready', items: itemsAbout(v.items, companyIds), team: v.team }); })
      .catch(() => { if (live) setLoad({ kind: 'failed' }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.tenantId, key]);
  const gone = (id: string) => setLoad((l) => (l.kind === 'ready' ? { ...l, items: l.items.filter((i) => i.id !== id) } : l));
  const update = (item: PassItem) => setLoad((l) => (l.kind === 'ready' ? { ...l, items: l.items.map((i) => (i.id === item.id ? item : i)) } : l));
  return [load, gone, update];
}

export function AlsoNeedsYou({ store, name, load, onGone, onUpdate, open }: { store: Store; name: string; load: Load; onGone: (id: string) => void; onUpdate: (item: PassItem) => void; open: (path: string) => void }) {
  if (load.kind === 'loading' || load.kind === 'failed') return null;
  // Nothing needs you (drawing c5-states.html): one line in the section's place, not a box of nothing (BUGS.md D187).
  if (load.items.length === 0) return <p className="none empties" id="a-needs-none">Nothing on {name} needs you. An item about them shows here when it reaches your list.</p>;
  const team = load.team.filter((m) => m.principalId !== store.principalId);
  return (
    <section className="sec needs" id="a-needs">
      <div className="sh"><h2>Also needs you</h2><span className="sl">{load.items.length === 1 ? 'One item on your list' : `${load.items.length} items on your list`}</span></div>
      <div className="sb needs-cards">
        {load.items.map((item) => (
          // The card is handed its item without the company: on the company's own record its line would say the head
          // again on every card (live, 7 Oct: Bet365's four cards each repeated "Handled by Will Fyler, 4 agreements in
          // force", BUGS.md D188). Only the card's line reads it; the list was filtered by it at load.
          <ItemCard key={item.id} item={{ ...item, organisation: null }} team={team} self={store.principalId} rank open={open}
            onGone={onGone} onUpdate={onUpdate}
            onDraft={(it) => open(`/items/${encodeURIComponent(it.id)}?store=${encodeURIComponent(it.store)}`)} />
        ))}
      </div>
    </section>
  );
}
